// ============================================
// Clinical Calibration — Doctor Training Module
// 3-phase workflow: Blind Diagnosis → Reveal → Prompt Maker
// NO framer-motion — pure CSS transitions + conditional rendering
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import { Beaker, MessageSquare, Pill, ChevronDown, Mic, Keyboard, Play, Send, X, ChevronUp, ClipboardList } from 'lucide-react';
import { STOCK_CASES } from '../../../data/stockCases';
import type { StockCase, DecisionPoint, TuningMessage } from '../../../types/clinicalCalibration';

// -------------------------------------------------------
// Formulary Data (MVP hardcoded)
// -------------------------------------------------------

interface FormularyItem {
  id: string;
  name: string;
  contents: string;
}

const SUPPLEMENTS: FormularyItem[] = [
  { id: 'sup-thyroid', name: 'Thyroid Support Bundle', contents: 'Selenium 200mcg, Zinc 30mg, Iodine 150mcg, L-Tyrosine 500mg' },
  { id: 'sup-iron', name: 'Iron Support', contents: 'Iron Bisglycinate 25mg, Vitamin C 200mg for absorption' },
  { id: 'sup-b12', name: 'B12 Methylcobalamin', contents: 'Methylcobalamin 5000mcg sublingual' },
  { id: 'sup-sleep', name: 'Sleep Protocol', contents: 'Magnesium Glycinate 400mg, L-Theanine 200mg, Apigenin 50mg' },
  { id: 'sup-adrenal', name: 'Adrenal Support', contents: 'Ashwagandha KSM-66 600mg, Rhodiola 200mg, Phosphatidylserine 100mg' },
  { id: 'sup-omega', name: 'Omega-3 Ultra', contents: 'EPA 1200mg, DHA 900mg — triglyceride form' },
  { id: 'sup-gut', name: 'Gut Restore', contents: 'L-Glutamine 5g, Zinc Carnosine 75mg, DGL 400mg, Probiotics 50B CFU' },
];

const RX: FormularyItem[] = [
  { id: 'rx-levo-low', name: 'Levothyroxine (Low)', contents: '25–50mcg daily — starting dose for subclinical hypothyroidism' },
  { id: 'rx-levo-std', name: 'Levothyroxine (Standard)', contents: '75–100mcg daily — standard replacement dose' },
  { id: 'rx-trt-topical', name: 'Testosterone Topical', contents: '1% gel, 50mg daily — transdermal application' },
  { id: 'rx-trt-inject', name: 'Testosterone Injectable', contents: 'Testosterone Cypionate 200mg/mL — weekly IM injection' },
  { id: 'rx-progesterone', name: 'Progesterone Oral', contents: 'Micronized progesterone 100–200mg — cyclical or daily' },
  { id: 'rx-dhea', name: 'DHEA Compounded', contents: 'DHEA 10–25mg compounded — adrenal precursor support' },
];

const LONGEVITY: FormularyItem[] = [
  { id: 'lon-nad', name: 'NAD+ IV', contents: 'NAD+ 250–500mg IV infusion — cellular energy and repair' },
  { id: 'lon-bpc', name: 'BPC-157', contents: 'BPC-157 500mcg subcutaneous — tissue repair peptide' },
  { id: 'lon-metformin', name: 'Metformin Off-Label', contents: 'Metformin 500mg — longevity / glucose optimization' },
  { id: 'lon-rapa', name: 'Rapamycin Cyclic', contents: 'Rapamycin 5mg weekly — mTOR modulation protocol' },
];

interface LabPanel {
  id: string;
  name: string;
  contents: string;
}

interface LabPanelGroupLocal {
  groupId: string;
  groupLabel: string;
  panels: LabPanel[];
}

const LAB_PANELS: LabPanelGroupLocal[] = [
  {
    groupId: 'thyroid',
    groupLabel: 'Thyroid',
    panels: [
      { id: 'lp-tsh', name: 'TSH', contents: 'Thyroid-stimulating hormone' },
      { id: 'lp-ft4', name: 'Free T4', contents: 'Free thyroxine' },
      { id: 'lp-ft3', name: 'Free T3', contents: 'Free triiodothyronine' },
      { id: 'lp-tpo', name: 'TPO Ab', contents: 'Thyroid peroxidase antibodies' },
      { id: 'lp-tgab', name: 'Thyroglobulin Ab', contents: 'Thyroglobulin antibodies' },
      { id: 'lp-rt3', name: 'Reverse T3', contents: 'Reverse triiodothyronine' },
    ],
  },
  {
    groupId: 'hormones',
    groupLabel: 'Hormones',
    panels: [
      { id: 'lp-e2', name: 'Estradiol', contents: 'Estradiol (E2)' },
      { id: 'lp-prog', name: 'Progesterone', contents: 'Serum progesterone' },
      { id: 'lp-tt', name: 'Total T', contents: 'Total testosterone' },
      { id: 'lp-ft', name: 'Free T', contents: 'Free testosterone' },
      { id: 'lp-dheas', name: 'DHEA-S', contents: 'Dehydroepiandrosterone sulfate' },
      { id: 'lp-shbg', name: 'SHBG', contents: 'Sex hormone-binding globulin' },
      { id: 'lp-cortam', name: 'Cortisol AM', contents: 'Morning cortisol' },
    ],
  },
  {
    groupId: 'metabolic',
    groupLabel: 'Metabolic',
    panels: [
      { id: 'lp-cbc', name: 'CBC', contents: 'Complete blood count' },
      { id: 'lp-cmp', name: 'CMP', contents: 'Comprehensive metabolic panel' },
      { id: 'lp-ferritin', name: 'Ferritin', contents: 'Serum ferritin (iron stores)' },
      { id: 'lp-vitd', name: 'Vit D', contents: 'Vitamin D, 25-OH' },
      { id: 'lp-b12', name: 'B12', contents: 'Vitamin B12' },
      { id: 'lp-folate', name: 'Folate', contents: 'Serum folate' },
      { id: 'lp-hba1c', name: 'HbA1c', contents: 'Hemoglobin A1c' },
      { id: 'lp-hscrp', name: 'hs-CRP', contents: 'High-sensitivity C-reactive protein' },
      { id: 'lp-homocys', name: 'Homocysteine', contents: 'Plasma homocysteine' },
    ],
  },
  {
    groupId: 'lipids',
    groupLabel: 'Lipids',
    panels: [
      { id: 'lp-lipid', name: 'Lipid Panel', contents: 'Total cholesterol, LDL, HDL, triglycerides' },
      { id: 'lp-apob', name: 'ApoB', contents: 'Apolipoprotein B' },
      { id: 'lp-lpa', name: 'Lp(a)', contents: 'Lipoprotein(a)' },
      { id: 'lp-oxldl', name: 'Oxidized LDL', contents: 'Oxidized low-density lipoprotein' },
    ],
  },
];

// -------------------------------------------------------
// Mock MedGemma Data (stock-001 Sarah M.)
// -------------------------------------------------------

const MOCK_MEDGEMMA = {
  diagnosis:
    'Functional hypothyroidism with concurrent vitamin D deficiency and iron depletion. TSH 3.8 above functional range (1.0–2.5) with Free T4 0.9 and Free T3 2.1 both below functional thresholds. Ferritin 22 indicates depleted iron stores impacting thyroid conversion. Vitamin D 18 is below conventional range. B12 310 is suboptimal. Cortisol 16.2 mildly elevated — consistent with HPA axis dysregulation.',
  protocol:
    'Tier 1: Thyroid Support Bundle ($89/mo) + Iron Support ($29/mo) + B12 Methylcobalamin ($19/mo). Tier 2: Add Vitamin D3+K2 5000 IU daily. Retest thyroid panel + ferritin + Vit D in 8 weeks. If TSH >4.5 on retest or symptoms worsen, escalate to provider for levothyroxine evaluation.',
  grade: 'B+' as const,
};

const MOCK_DECISION_POINTS: DecisionPoint[] = [
  { area: 'Primary Dx', drCPosition: 'Subclinical hypothyroidism', medgemmaPosition: 'Functional hypothyroidism with multi-system involvement', alignment: 'aligned', clinicalWeight: 0.20 },
  { area: 'Key Lab Read', drCPosition: 'TSH 3.8, low Free T3/T4', medgemmaPosition: 'TSH 3.8 above functional, Free T4 and Free T3 below functional thresholds', alignment: 'aligned', clinicalWeight: 0.15 },
  { area: 'Secondary Findings', drCPosition: 'Vitamin D deficiency noted', medgemmaPosition: 'Vitamin D 18 below conventional + Ferritin 22 depleted + B12 310 suboptimal', alignment: 'delta', deltaNote: 'Dr. C missed iron depletion and suboptimal B12 as contributing factors', clinicalWeight: 0.10 },
  { area: 'Supplement-First', drCPosition: 'Thyroid support recommended', medgemmaPosition: 'Thyroid Support Bundle + Iron Support + B12 as Tier 1 protocol', alignment: 'aligned', clinicalWeight: 0.15 },
  { area: 'Product Selection', drCPosition: 'Thyroid bundle only', medgemmaPosition: 'Thyroid + Iron + B12 bundle — addresses all deficiencies', alignment: 'delta', deltaNote: 'Dr. C did not include Iron Support or B12 in initial protocol', clinicalWeight: 0.10 },
  { area: 'Escalation Path', drCPosition: 'Retest in 8 weeks', medgemmaPosition: 'Retest 8 weeks; escalate to provider if TSH >4.5 or symptoms worsen', alignment: 'aligned', clinicalWeight: 0.10 },
  { area: 'Missed Signals', drCPosition: 'Cortisol pattern not addressed', medgemmaPosition: 'Cortisol 16.2 mildly elevated — HPA axis dysregulation consistent with wired-tired pattern', alignment: 'both_missed', deltaNote: 'Neither addressed adrenal support despite wired-tired symptom and elevated cortisol', clinicalWeight: 0.08 },
  { area: 'Cost Awareness', drCPosition: 'Patient is cost-sensitive', medgemmaPosition: 'Tier 1 total $137/mo — affordable entry point for teacher salary', alignment: 'aligned', clinicalWeight: 0.07 },
  { area: 'Evidence Grade', drCPosition: 'Standard functional medicine approach', medgemmaPosition: 'Grade B+ — well-supported by functional ranges, family history, symptom correlation', alignment: 'aligned', clinicalWeight: 0.05 },
];

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------

function getStatusColor(status: string): string {
  switch (status) {
    case 'below_conventional':
    case 'above_conventional':
      return 'text-red-400';
    case 'below_functional':
    case 'above_functional':
    case 'elevated':
      return 'text-amber-400';
    case 'optimal':
      return 'text-green-400';
    default:
      return 'text-text-secondary';
  }
}

function getTagCategoryColor(category: string): string {
  switch (category) {
    case 'symptom': return 'bg-red-500/15 text-red-300 border-red-500/30';
    case 'history': return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'medication': return 'bg-purple-500/15 text-purple-300 border-purple-500/30';
    case 'concern': return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'lifestyle': return 'bg-green-500/15 text-green-300 border-green-500/30';
    default: return 'bg-bg-tertiary text-text-secondary border-bg-tertiary';
  }
}

// -------------------------------------------------------
// Component
// -------------------------------------------------------

export function ClinicalCalibration() {
  // --- Case state ---
  const [caseSource, setCaseSource] = useState<'random' | 'ran_test' | 'stock'>('stock');
  const [activeCase, setActiveCase] = useState<string>('stock-001');
  const [caseData, setCaseData] = useState<StockCase | null>(null);
  const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);

  // --- Phase ---
  const [phase, setPhase] = useState<'diagnose' | 'reveal' | 'tune'>('diagnose');

  // --- Phase 1 tabs ---
  const [inputTab, setInputTab] = useState<'labs' | 'chat' | 'formulary'>('labs');
  const [formularyGroup, setFormularyGroup] = useState<'supplements' | 'rx' | 'longevity' | 'laborders'>('supplements');

  // --- Collected evidence ---
  const [flaggedLabs, setFlaggedLabs] = useState<Set<string>>(new Set());
  const [chatNotes, setChatNotes] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedLabOrders, setSelectedLabOrders] = useState<Set<string>>(new Set());

  // --- Assessment ---
  const [diagnosis, setDiagnosis] = useState('');
  const [inputMode, setInputMode] = useState<'type' | 'voice'>('type');

  // --- Evidence shelf ---
  const [shelfOpen, setShelfOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);

  // --- Phase 3 ---
  const [tuneMessages, setTuneMessages] = useState<TuningMessage[]>([]);
  const [tuneInput, setTuneInput] = useState('');

  // --- Load case data ---
  useEffect(() => {
    if (caseSource === 'stock') {
      const found = STOCK_CASES.find((c) => c.caseId === activeCase);
      setCaseData(found ?? null);
    } else {
      setCaseData(null);
    }
  }, [caseSource, activeCase]);

  // --- Reset evidence when case changes ---
  useEffect(() => {
    setFlaggedLabs(new Set());
    setChatNotes(new Set());
    setSelectedProducts(new Set());
    setSelectedLabOrders(new Set());
    setDiagnosis('');
    setPhase('diagnose');
    setTuneMessages([]);
    setTuneInput('');
    setShelfOpen(false);
  }, [activeCase]);

  // --- Scroll direction detection for shelf ---
  const handleContentScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const currentTop = scrollRef.current.scrollTop;
    const delta = currentTop - lastScrollTop.current;
    if (delta > 12) {
      setShelfOpen(false);
    } else if (delta < -12) {
      setShelfOpen(true);
    }
    lastScrollTop.current = currentTop;
  }, []);

  // --- Toggle helpers ---
  const toggleFlaggedLab = useCallback((biomarker: string) => {
    setFlaggedLabs((prev) => {
      const next = new Set(prev);
      if (next.has(biomarker)) next.delete(biomarker);
      else next.add(biomarker);
      return next;
    });
  }, []);

  const toggleChatNote = useCallback((tag: string) => {
    setChatNotes((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const toggleProduct = useCallback((id: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleLabOrder = useCallback((id: string) => {
    setSelectedLabOrders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Submit & Compare ---
  const canSubmit = diagnosis.trim().length > 0 || flaggedLabs.size > 0 || chatNotes.size > 0 || selectedProducts.size > 0 || selectedLabOrders.size > 0;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    setPhase('reveal');
  }, [canSubmit]);

  // --- Phase 3 init ---
  const handleStartTuning = useCallback(() => {
    const alignedCount = MOCK_DECISION_POINTS.filter((d) => d.alignment === 'aligned').length;
    const deltaCount = MOCK_DECISION_POINTS.filter((d) => d.alignment === 'delta').length;
    const bothMissed = MOCK_DECISION_POINTS.filter((d) => d.alignment === 'both_missed').length;

    const systemMsg: TuningMessage = {
      role: 'system',
      text: `Calibration Summary: ${alignedCount}/9 aligned, ${deltaCount} deltas, ${bothMissed} both missed. Grade: ${MOCK_MEDGEMMA.grade}. Ready for fine-tuning instructions.`,
      timestamp: new Date().toISOString(),
    };

    const analysisMsg: TuningMessage = {
      role: 'system',
      text: `Delta Analysis:\n\n1. Secondary Findings: You missed iron depletion (Ferritin 22) and suboptimal B12 (310) as contributing factors to the fatigue presentation. MedGemma flagged both.\n\n2. Product Selection: Initial protocol only included Thyroid Support Bundle. Adding Iron Support and B12 addresses the full deficiency picture.\n\n3. Both Missed — Adrenal Support: Neither you nor MedGemma addressed the elevated cortisol (16.2) and wired-tired pattern with targeted adrenal support. Consider adding Ashwagandha/Rhodiola protocol.\n\nWould you like to adjust any of these decision points?`,
      timestamp: new Date().toISOString(),
    };

    setTuneMessages([systemMsg, analysisMsg]);
    setPhase('tune');
  }, []);

  const handleTuneSend = useCallback(() => {
    if (!tuneInput.trim()) return;
    const msg: TuningMessage = {
      role: 'physician',
      text: tuneInput.trim(),
      timestamp: new Date().toISOString(),
    };
    setTuneMessages((prev) => [...prev, msg]);
    setTuneInput('');

    // Mock system reply after brief delay
    setTimeout(() => {
      const reply: TuningMessage = {
        role: 'system',
        text: 'Noted. I will incorporate this adjustment into the model calibration. This instruction will be applied during the next training sync. Is there anything else you would like to fine-tune?',
        timestamp: new Date().toISOString(),
      };
      setTuneMessages((prev) => [...prev, reply]);
    }, 800);
  }, [tuneInput]);

  // --- Total evidence count ---
  const totalEvidence = flaggedLabs.size + chatNotes.size + selectedProducts.size + selectedLabOrders.size;

  // --- Case dropdown options ---
  const caseGroups = [
    { source: 'stock' as const, label: 'Stock Patients', cases: STOCK_CASES.map((c) => ({ id: c.caseId, label: c.label })) },
    { source: 'ran_test' as const, label: 'Ran Tests', cases: [] as { id: string; label: string }[] },
    { source: 'random' as const, label: 'Random', cases: [] as { id: string; label: string }[] },
  ];

  // --- Formulary items for the active sub-tab ---
  const getFormularyItems = (): FormularyItem[] => {
    switch (formularyGroup) {
      case 'supplements': return SUPPLEMENTS;
      case 'rx': return RX;
      case 'longevity': return LONGEVITY;
      case 'laborders': return [];
      default: return [];
    }
  };

  // --- Close dropdown on outside click ---
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCaseDropdownOpen(false);
      }
    }
    if (caseDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [caseDropdownOpen]);

  // ==========================================================
  // RENDER: Phase 1 — Blind Diagnosis
  // ==========================================================
  const renderDiagnosePhase = () => {
    if (!caseData) {
      return (
        <div className="bg-bg-card rounded-xl border border-bg-tertiary p-8 text-center">
          <p className="text-base text-text-muted">Select a case above to begin the blind diagnosis.</p>
        </div>
      );
    }

    return (
      <div className="space-y-0">
        {/* Phase 1 tabs */}
        <div className="flex gap-1 border-b border-bg-tertiary pb-1">
          <button
            onClick={() => setInputTab('labs')}
            className={`flex items-center gap-2 px-4 py-2 text-base font-medium transition-colors ${
              inputTab === 'labs' ? 'text-gold border-b-2 border-gold' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Beaker size={16} />
            Labs
          </button>
          <button
            onClick={() => setInputTab('chat')}
            className={`flex items-center gap-2 px-4 py-2 text-base font-medium transition-colors ${
              inputTab === 'chat' ? 'text-gold border-b-2 border-gold' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <MessageSquare size={16} />
            Chat
          </button>
          <button
            onClick={() => setInputTab('formulary')}
            className={`flex items-center gap-2 px-4 py-2 text-base font-medium transition-colors ${
              inputTab === 'formulary' ? 'text-gold border-b-2 border-gold' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Pill size={16} />
            Formulary
          </button>
        </div>

        {/* Evidence Shelf */}
        <div className="bg-bg-elevated border-b border-bg-tertiary">
          {/* Handle bar for tap toggle */}
          <button
            onClick={() => setShelfOpen((p) => !p)}
            className="w-full flex flex-col items-center py-1.5 cursor-pointer group"
          >
            <div className="w-8 h-[3px] rounded-full bg-text-muted/40 group-hover:bg-gold/60 transition-colors" />
            {totalEvidence > 0 && !shelfOpen && (
              <span className="text-xs text-text-muted mt-0.5">{totalEvidence} item{totalEvidence !== 1 ? 's' : ''} collected</span>
            )}
          </button>

          {/* Shelf content with max-height transition */}
          <div
            className={`overflow-hidden transition-all duration-200 ${
              shelfOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-4 pb-3 flex flex-wrap gap-1.5">
              {/* Flagged labs — red chips */}
              {Array.from(flaggedLabs).map((lab) => (
                <button
                  key={`fl-${lab}`}
                  onClick={() => toggleFlaggedLab(lab)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 transition-colors"
                >
                  {lab}
                  <X size={12} />
                </button>
              ))}
              {/* Chat notes — amber chips */}
              {Array.from(chatNotes).map((note) => (
                <button
                  key={`cn-${note}`}
                  onClick={() => toggleChatNote(note)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
                >
                  {note}
                  <X size={12} />
                </button>
              ))}
              {/* Products — green chips */}
              {Array.from(selectedProducts).map((pid) => {
                const item = [...SUPPLEMENTS, ...RX, ...LONGEVITY].find((p) => p.id === pid);
                return (
                  <button
                    key={`sp-${pid}`}
                    onClick={() => toggleProduct(pid)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm bg-green-500/15 text-green-300 border border-green-500/30 hover:bg-green-500/25 transition-colors"
                  >
                    {item?.name ?? pid}
                    <X size={12} />
                  </button>
                );
              })}
              {/* Lab orders — green chips with clipboard icon */}
              {Array.from(selectedLabOrders).map((lid) => {
                const panel = LAB_PANELS.flatMap((g) => g.panels).find((p) => p.id === lid);
                return (
                  <button
                    key={`lo-${lid}`}
                    onClick={() => toggleLabOrder(lid)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm bg-green-500/15 text-green-300 border border-green-500/30 hover:bg-green-500/25 transition-colors"
                  >
                    <ClipboardList size={12} />
                    {panel?.name ?? lid}
                    <X size={12} />
                  </button>
                );
              })}
              {totalEvidence === 0 && (
                <span className="text-sm text-text-muted">No evidence collected yet. Flag labs, tag chat notes, or select formulary items.</span>
              )}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div
          ref={scrollRef}
          onScroll={handleContentScroll}
          className="max-h-[480px] overflow-y-auto"
        >
          {/* Labs Tab */}
          {inputTab === 'labs' && (
            <div className="bg-bg-card rounded-b-xl border border-t-0 border-bg-tertiary">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-bg-tertiary">
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted">Biomarker</th>
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted">Value</th>
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted">Unit</th>
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted hidden sm:table-cell">Std Range</th>
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted hidden md:table-cell">Functional</th>
                    <th className="px-4 py-3 text-sm font-semibold text-text-muted w-10">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {caseData.labs.map((lab) => {
                    const isFlagged = flaggedLabs.has(lab.biomarker);
                    return (
                      <tr
                        key={lab.biomarker}
                        onClick={() => toggleFlaggedLab(lab.biomarker)}
                        className={`cursor-pointer transition-colors hover:bg-bg-tertiary/50 ${
                          isFlagged ? 'border-l-2 border-l-red-500 bg-red-500/5' : 'border-l-2 border-l-transparent'
                        }`}
                      >
                        <td className="px-4 py-3 text-base text-text-primary font-medium">{lab.biomarker}</td>
                        <td className={`px-4 py-3 text-base font-mono font-semibold ${getStatusColor(lab.status)}`}>
                          {lab.value}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">{lab.unit}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary hidden sm:table-cell">{lab.conventionalRange}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary hidden md:table-cell">{lab.functionalRange}</td>
                        <td className="px-4 py-3 text-center">
                          {isFlagged && <span className="text-red-400 text-base">&#9873;</span>}
                          {!isFlagged && lab.trigger && <span className="text-amber-400/40 text-sm">&#9679;</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Chat Tab */}
          {inputTab === 'chat' && (
            <div className="bg-bg-card rounded-b-xl border border-t-0 border-bg-tertiary p-4 space-y-3">
              {caseData.transcript.map((msg, idx) => {
                const messageTags = caseData.tags.filter((t) => t.messageIndex === idx);
                return (
                  <div key={idx}>
                    <div
                      className={`rounded-lg px-4 py-3 ${
                        msg.role === 'patient'
                          ? 'border-l-3 border-l-green-500 bg-green-500/5'
                          : 'border-l-3 border-l-gold bg-gold/5'
                      }`}
                    >
                      <p className="text-xs font-mono font-semibold text-text-muted mb-1 uppercase">
                        {msg.role}
                      </p>
                      <p className="text-base text-text-primary leading-relaxed">{msg.text}</p>
                    </div>
                    {/* Tag chips for patient messages */}
                    {msg.role === 'patient' && messageTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5 ml-4">
                        {messageTags.map((t) => {
                          const isActive = chatNotes.has(t.tag);
                          return (
                            <button
                              key={t.tag}
                              onClick={() => toggleChatNote(t.tag)}
                              className={`px-2.5 py-1 rounded-lg text-sm border transition-colors ${
                                isActive
                                  ? 'bg-amber-500/25 text-amber-200 border-amber-500/50 font-semibold'
                                  : getTagCategoryColor(t.category) + ' hover:opacity-80'
                              }`}
                            >
                              {t.tag}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Formulary Tab */}
          {inputTab === 'formulary' && (
            <div className="bg-bg-card rounded-b-xl border border-t-0 border-bg-tertiary">
              {/* Formulary sub-tabs */}
              <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-bg-tertiary overflow-x-auto">
                {([
                  { key: 'supplements' as const, label: 'Supplements' },
                  { key: 'rx' as const, label: 'Rx' },
                  { key: 'longevity' as const, label: 'Longevity' },
                  { key: 'laborders' as const, label: 'Lab Orders' },
                ] as const).map((sub) => (
                  <button
                    key={sub.key}
                    onClick={() => setFormularyGroup(sub.key)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                      formularyGroup === sub.key
                        ? 'bg-gold/15 text-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              {/* Formulary items */}
              {formularyGroup !== 'laborders' ? (
                <div className="p-4 space-y-2">
                  {getFormularyItems().map((item) => {
                    const isSelected = selectedProducts.has(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleProduct(item.id)}
                        className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-colors ${
                          isSelected
                            ? 'bg-green-500/10 border-green-500/40'
                            : 'bg-bg-elevated border-bg-tertiary hover:border-text-muted/30'
                        }`}
                      >
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-green-500 border-green-500' : 'border-text-muted/40'
                        }`}>
                          {isSelected && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-base font-medium ${isSelected ? 'text-green-300' : 'text-text-primary'}`}>
                            {item.name}
                          </p>
                          <p className="text-sm text-text-muted mt-0.5">{item.contents}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Lab Orders — grouped by panel */
                <div className="p-4 space-y-4">
                  {LAB_PANELS.map((group) => (
                    <div key={group.groupId}>
                      <h5 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-2">{group.groupLabel}</h5>
                      <div className="space-y-1.5">
                        {group.panels.map((panel) => {
                          const isSelected = selectedLabOrders.has(panel.id);
                          return (
                            <button
                              key={panel.id}
                              onClick={() => toggleLabOrder(panel.id)}
                              className={`w-full text-left flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-colors ${
                                isSelected
                                  ? 'bg-green-500/10 border-green-500/40'
                                  : 'bg-bg-elevated border-bg-tertiary hover:border-text-muted/30'
                              }`}
                            >
                              <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected ? 'bg-green-500 border-green-500' : 'border-text-muted/40'
                              }`}>
                                {isSelected && (
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-base font-medium ${isSelected ? 'text-green-300' : 'text-text-primary'}`}>
                                  {panel.name}
                                </p>
                                <p className="text-sm text-text-muted">{panel.contents}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assessment input */}
        <div className="bg-bg-card rounded-xl border border-bg-tertiary p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-base font-semibold text-text-primary">Your Assessment</h4>
            <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-0.5">
              <button
                onClick={() => setInputMode('type')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  inputMode === 'type' ? 'bg-bg-card text-gold' : 'text-text-muted hover:text-text-primary'
                }`}
              >
                <Keyboard size={14} />
                Type
              </button>
              <button
                onClick={() => setInputMode('voice')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  inputMode === 'voice' ? 'bg-bg-card text-gold' : 'text-text-muted hover:text-text-primary'
                }`}
              >
                <Mic size={14} />
                Voice
              </button>
            </div>
          </div>

          {inputMode === 'type' ? (
            <textarea
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Enter your blind diagnosis, suspected conditions, and recommended protocol..."
              rows={4}
              className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-bg-tertiary text-base text-text-primary placeholder:text-text-muted resize-none focus:outline-none focus:border-gold transition-colors"
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <button className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center hover:bg-red-500/30 transition-colors">
                <Mic size={28} className="text-red-400" />
              </button>
              <p className="text-sm text-text-muted">Tap to record (MVP — UI only)</p>
            </div>
          )}
        </div>

        {/* Submit button */}
        <div className="mt-4">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base font-semibold transition-colors ${
              canSubmit
                ? 'bg-gold text-bg-primary hover:bg-gold/90'
                : 'bg-bg-tertiary text-text-muted cursor-not-allowed'
            }`}
          >
            <Play size={18} />
            Submit &amp; Compare
          </button>
        </div>
      </div>
    );
  };

  // ==========================================================
  // RENDER: Phase 2 — Reveal Comparison
  // ==========================================================
  const renderRevealPhase = () => (
    <div className="space-y-6">
      {/* Dr. C's Assessment */}
      <div className="bg-blue-500/5 rounded-xl border border-blue-500/20 p-6">
        <h4 className="text-base font-semibold text-blue-300 mb-3 flex items-center gap-2">
          <Beaker size={18} />
          Your Assessment
        </h4>
        {diagnosis.trim() ? (
          <p className="text-base text-text-primary leading-relaxed">{diagnosis}</p>
        ) : (
          <p className="text-sm text-text-muted italic">No written diagnosis submitted — evidence shelf items only.</p>
        )}
        {totalEvidence > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-blue-500/20">
            {Array.from(flaggedLabs).map((lab) => (
              <span key={lab} className="px-2 py-0.5 rounded text-sm bg-red-500/15 text-red-300">{lab}</span>
            ))}
            {Array.from(chatNotes).map((note) => (
              <span key={note} className="px-2 py-0.5 rounded text-sm bg-amber-500/15 text-amber-300">{note}</span>
            ))}
            {Array.from(selectedProducts).map((pid) => {
              const item = [...SUPPLEMENTS, ...RX, ...LONGEVITY].find((p) => p.id === pid);
              return <span key={pid} className="px-2 py-0.5 rounded text-sm bg-green-500/15 text-green-300">{item?.name ?? pid}</span>;
            })}
            {Array.from(selectedLabOrders).map((lid) => {
              const panel = LAB_PANELS.flatMap((g) => g.panels).find((p) => p.id === lid);
              return <span key={lid} className="px-2 py-0.5 rounded text-sm bg-green-500/15 text-green-300">{panel?.name ?? lid}</span>;
            })}
          </div>
        )}
      </div>

      {/* MedGemma Assessment */}
      <div className="bg-bg-elevated rounded-xl border border-bg-tertiary p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-purple-400">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            MedGemma Assessment
          </h4>
          <span className={`px-3 py-1 rounded-lg text-sm font-mono font-bold ${
            MOCK_MEDGEMMA.grade.startsWith('A') ? 'bg-green-500/15 text-green-300'
              : MOCK_MEDGEMMA.grade.startsWith('B') ? 'bg-amber-500/15 text-amber-300'
                : 'bg-red-500/15 text-red-300'
          }`}>
            {MOCK_MEDGEMMA.grade}
          </span>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-1">Diagnosis</p>
            <p className="text-base text-text-primary leading-relaxed">{MOCK_MEDGEMMA.diagnosis}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-1">Protocol</p>
            <p className="text-base text-text-secondary leading-relaxed">{MOCK_MEDGEMMA.protocol}</p>
          </div>
        </div>
      </div>

      {/* Decision Points */}
      <div>
        <h4 className="text-lg font-semibold text-text-primary mb-4">Decision Points</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {MOCK_DECISION_POINTS.map((dp) => (
            <div
              key={dp.area}
              className={`rounded-xl border p-4 ${
                dp.alignment === 'aligned'
                  ? 'border-l-3 border-l-emerald-500 border-t border-r border-b border-bg-tertiary bg-emerald-500/5'
                  : dp.alignment === 'delta'
                    ? 'border-l-3 border-l-red-500 border-t border-r border-b border-bg-tertiary bg-red-500/5'
                    : 'border-l-3 border-l-amber-500 border-t border-r border-b border-bg-tertiary bg-amber-500/5'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-base font-semibold text-text-primary">{dp.area}</h5>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  dp.alignment === 'aligned'
                    ? 'bg-emerald-500/20 text-emerald-300'
                    : dp.alignment === 'delta'
                      ? 'bg-red-500/20 text-red-300'
                      : 'bg-amber-500/20 text-amber-300'
                }`}>
                  {dp.alignment === 'aligned' ? '\u2713 Aligned' : dp.alignment === 'delta' ? '\u2717 Delta' : '\u26A0 Both Missed'}
                </span>
              </div>
              <div className="space-y-1.5">
                <div>
                  <p className="text-xs text-text-muted font-mono">YOU</p>
                  <p className="text-sm text-text-secondary">{dp.drCPosition}</p>
                </div>
                <div>
                  <p className="text-xs text-text-muted font-mono">MEDGEMMA</p>
                  <p className="text-sm text-text-secondary">{dp.medgemmaPosition}</p>
                </div>
                {dp.deltaNote && (
                  <p className="text-sm text-amber-300/80 mt-1 pt-1 border-t border-bg-tertiary">{dp.deltaNote}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fine-Tune Button */}
      <button
        onClick={handleStartTuning}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gold text-bg-primary font-semibold text-base hover:bg-gold/90 transition-colors"
      >
        Fine-Tune MedGemma
        <ChevronDown size={18} className="-rotate-90" />
      </button>
    </div>
  );

  // ==========================================================
  // RENDER: Phase 3 — Prompt Maker
  // ==========================================================
  const renderTunePhase = () => {
    const quickActions = [
      'Accept all deltas',
      'My approach was correct for this case',
      'Add adrenal support to protocol',
      'Iron is optional here',
      'Increase escalation threshold',
      'Lower cost sensitivity weight',
    ];

    return (
      <div className="space-y-4">
        {/* Chat messages */}
        <div className="bg-bg-card rounded-xl border border-bg-tertiary p-4 max-h-[480px] overflow-y-auto space-y-3">
          {tuneMessages.map((msg, idx) => (
            <div
              key={idx}
              className={`rounded-lg px-4 py-3 ${
                msg.role === 'system'
                  ? 'bg-bg-elevated border border-bg-tertiary'
                  : 'bg-gold/10 border border-gold/20'
              }`}
            >
              <p className="text-xs font-mono font-semibold text-text-muted mb-1 uppercase">
                {msg.role === 'system' ? 'SYSTEM' : 'DR. C'}
              </p>
              <p className="text-base text-text-primary leading-relaxed whitespace-pre-line">{msg.text}</p>
            </div>
          ))}
        </div>

        {/* Quick action chips */}
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action) => (
            <button
              key={action}
              onClick={() => setTuneInput(action)}
              className="px-3 py-1.5 rounded-lg text-sm bg-bg-tertiary text-text-secondary border border-bg-tertiary hover:border-gold/40 hover:text-gold transition-colors"
            >
              {action}
            </button>
          ))}
        </div>

        {/* Chat input */}
        <div className="flex gap-3">
          <input
            type="text"
            value={tuneInput}
            onChange={(e) => setTuneInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleTuneSend(); }}
            placeholder="Type fine-tuning instruction..."
            className="flex-1 px-4 py-3 rounded-xl bg-bg-tertiary border border-bg-tertiary text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors"
          />
          <button
            onClick={handleTuneSend}
            disabled={!tuneInput.trim()}
            className={`flex items-center justify-center px-5 py-3 rounded-xl font-semibold text-base transition-colors ${
              tuneInput.trim()
                ? 'bg-gold text-bg-primary hover:bg-gold/90'
                : 'bg-bg-tertiary text-text-muted cursor-not-allowed'
            }`}
          >
            <Send size={18} />
          </button>
        </div>

        {/* Back to comparison */}
        <button
          onClick={() => setPhase('reveal')}
          className="flex items-center gap-2 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronUp size={14} />
          Back to Comparison
        </button>
      </div>
    );
  };

  // ==========================================================
  // MAIN RENDER
  // ==========================================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Beaker size={24} className="text-gold" />
          <div>
            <h3 className="text-2xl font-display text-gold">Clinical Calibration</h3>
            <p className="text-base text-text-secondary mt-1">
              Blind diagnosis training — compare your assessment against MedGemma
            </p>
          </div>
        </div>

        {/* Phase indicator */}
        <div className="flex items-center gap-2">
          {(['diagnose', 'reveal', 'tune'] as const).map((p, idx) => (
            <div key={p} className="flex items-center gap-2">
              {idx > 0 && <div className="w-6 h-px bg-bg-tertiary" />}
              <button
                onClick={() => {
                  // Only allow going back, not skipping forward
                  if (p === 'diagnose') setPhase('diagnose');
                  else if (p === 'reveal' && phase !== 'diagnose') setPhase('reveal');
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  phase === p
                    ? 'bg-gold/15 text-gold border border-gold/30'
                    : phase === 'tune' || (phase === 'reveal' && p === 'diagnose')
                      ? 'bg-bg-tertiary text-text-muted hover:text-text-primary cursor-pointer'
                      : 'bg-bg-tertiary text-text-muted/50 cursor-default'
                }`}
              >
                {p === 'diagnose' ? '1. Diagnose' : p === 'reveal' ? '2. Compare' : '3. Tune'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Case Selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setCaseDropdownOpen((p) => !p)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-bg-card border border-bg-tertiary text-left hover:border-gold/30 transition-colors"
        >
          <div className="flex-1 min-w-0">
            <p className="text-xs text-text-muted uppercase tracking-wider mb-0.5">Active Case</p>
            <p className="text-base text-text-primary font-medium truncate">
              {caseData ? caseData.label : 'Select a case...'}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3">
            {caseData && (
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                caseData.acuity === 'high' ? 'bg-red-500/15 text-red-300'
                  : caseData.acuity === 'complex' ? 'bg-amber-500/15 text-amber-300'
                    : 'bg-green-500/15 text-green-300'
              }`}>
                {caseData.acuity}
              </span>
            )}
            <ChevronDown size={18} className={`text-text-muted transition-transform duration-200 ${caseDropdownOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Dropdown */}
        {caseDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-bg-elevated rounded-xl border border-bg-tertiary shadow-xl z-20 max-h-80 overflow-y-auto">
            {caseGroups.map((group) => (
              <div key={group.source}>
                <p className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider bg-bg-tertiary/50">
                  {group.label}
                </p>
                {group.cases.length > 0 ? (
                  group.cases.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setCaseSource(group.source);
                        setActiveCase(c.id);
                        setCaseDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-base hover:bg-bg-tertiary/50 transition-colors ${
                        activeCase === c.id && caseSource === group.source
                          ? 'text-gold font-medium'
                          : 'text-text-primary'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-3 text-sm text-text-muted italic">
                    {group.source === 'ran_test' ? 'No completed test runs yet' : 'Random case generation coming soon'}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase Content */}
      {phase === 'diagnose' && renderDiagnosePhase()}
      {phase === 'reveal' && renderRevealPhase()}
      {phase === 'tune' && renderTunePhase()}
    </div>
  );
}
